import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function FadeSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function AboutContent() {
  const { t } = useTranslation();

  const valuesKeys = [0, 1, 2, 3];

  return (
    <main className="pt-16">
      {/* Page hero */}
      <section className="bg-navy py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeSection>
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("about.hero.label")}
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight max-w-2xl">
              {t("about.hero.title")}
            </h1>
          </FadeSection>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <FadeSection>
              <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
                {t("about.mission.label")}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6 leading-snug">
                {t("about.mission.title")}
              </h2>
              <hr className="border-cool-gray-200 mb-6 w-16" />
              <p className="text-navy/70 text-[15px] leading-relaxed mb-5">
                {t("about.mission.p1")}
              </p>
              <p className="text-navy/70 text-[15px] leading-relaxed mb-5">
                {t("about.mission.p2")}
              </p>
              <p className="text-navy/70 text-[15px] leading-relaxed">
                {t("about.mission.p3")}
              </p>
            </FadeSection>
            <FadeSection delay={200}>
              <div className="w-full h-72 md:h-80 overflow-hidden rounded-lg mb-8">
                <img
                  src="https://public.readdy.ai/ai/img_res/e9ab7cf2f1e6bc84a6df383b5b0e3c18.jpg"
                  alt="EU Next Grants office building"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="grid grid-cols-3 gap-6">
                {[
                  { number: "", labelKey: "about.stats.s1" },
                  { number: "", labelKey: "about.stats.s2" },
                  { number: "", labelKey: "about.stats.s3" },
                ].map((stat) => (
                  <div key={stat.labelKey} className="text-center py-5 bg-cool-gray-50 rounded-md">
                    <p className="text-2xl font-bold text-navy">{stat.number}</p>
                    <p className="text-xs text-navy/50 mt-1">{t(stat.labelKey)}</p>
                  </div>
                ))}
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-navy py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeSection>
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("about.values.label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 leading-snug">
              {t("about.values.title")}
            </h2>
          </FadeSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valuesKeys.map((i) => (
              <FadeSection key={i} delay={i * 100}>
                <div className="border-l border-gold/40 pl-5">
                  <h3 className="text-sm font-semibold text-white mb-2">{t(`about.values.${i}.title`)}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{t(`about.values.${i}.desc`)}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
